import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Loader2,
  Search,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from 'lucide-react';
import { adminTicketsAPI, adminLookupAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import ContactPicker from './ContactPicker';
import MemberDayTimeline from './MemberDayTimeline';

const NOTE_TYPES = [
  { value: 'discussion', label: 'Discussion' },
  { value: 'internal', label: 'Internal Note' },
  { value: 'resolution', label: 'Resolution' },
];

const DURATIONS = [
  { label: '15 min', minutes: 15 },
  { label: '30 min', minutes: 30 },
  { label: '1 hr', minutes: 60 },
  { label: '2 hr', minutes: 120 },
  { label: '4 hr', minutes: 240 },
];

// CW Reminders are a fixed lookup; the REST endpoint isn't exposed, so we
// stick to the IDs we've verified against this install.
const REMINDERS = [
  { label: 'None', minutes: null },
  { label: '10 min', minutes: 10 },
  { label: '15 min', minutes: 15 },
  { label: '1 hour', minutes: 60 },
];

function nextTopOfHourLocal() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminNewTicketModal({ onClose, initialCompanyId = null }) {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);

  // Company picker
  const [companySearch, setCompanySearch] = useState('');
  const [company, setCompany] = useState(null);
  const [showCompanyResults, setShowCompanyResults] = useState(false);
  const debouncedCompanySearch = useDebounce(companySearch, 300);

  const { data: companiesData, isFetching: searchingCompanies } = useQuery({
    queryKey: ['admin-ticket-company-search', debouncedCompanySearch],
    queryFn: () => adminLookupAPI.companies(debouncedCompanySearch),
    enabled: debouncedCompanySearch.trim().length >= 2 && !company,
    staleTime: 30_000,
  });
  const companyResults = companiesData?.data?.companies || companiesData?.data?.items || [];

  // Lookups
  const { data: boardsData } = useQuery({
    queryKey: ['admin-lookup-boards'],
    queryFn: () => adminLookupAPI.boards(),
  });
  const boards = boardsData?.data?.items || [];

  const { data: prioritiesData } = useQuery({
    queryKey: ['admin-lookup-priorities'],
    queryFn: () => adminLookupAPI.priorities(),
  });
  const priorities = prioritiesData?.data?.items || [];

  const { data: sourcesData } = useQuery({
    queryKey: ['admin-lookup-sources'],
    queryFn: () => adminLookupAPI.sources(),
  });
  const sources = sourcesData?.data?.items || [];

  const { data: membersData } = useQuery({
    queryKey: ['admin-lookup-members'],
    queryFn: () => adminLookupAPI.members(),
  });
  const members = membersData?.data?.items || [];

  // Form state
  const [contact, setContact] = useState(null);
  const [boardId, setBoardId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [subTypeId, setSubTypeId] = useState('');
  const [itemId, setItemId] = useState('');
  const [priorityId, setPriorityId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [agreementId, setAgreementId] = useState('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [noteType, setNoteType] = useState('discussion');
  const [error, setError] = useState(null);
  const [confirmingConflict, setConfirmingConflict] = useState(false);

  // Cascaded lookups
  const { data: statusesData } = useQuery({
    queryKey: ['admin-lookup-statuses', boardId],
    queryFn: () => adminLookupAPI.boardStatuses(boardId),
    enabled: !!boardId,
  });
  const statuses = statusesData?.data?.items || [];

  const { data: typesData } = useQuery({
    queryKey: ['admin-lookup-types', boardId],
    queryFn: () => adminLookupAPI.boardTypes(boardId),
    enabled: !!boardId,
  });
  const types = typesData?.data?.items || [];

  const { data: subTypesData } = useQuery({
    queryKey: ['admin-lookup-subtypes', boardId, typeId],
    queryFn: () => adminLookupAPI.boardSubTypes(boardId, typeId),
    enabled: !!boardId && !!typeId,
  });
  const subTypes = subTypesData?.data?.items || [];

  const { data: itemsData } = useQuery({
    queryKey: ['admin-lookup-items', boardId],
    queryFn: () => adminLookupAPI.boardItems(boardId),
    enabled: !!boardId,
  });
  const items = itemsData?.data?.items || [];

  const { data: agreementsData } = useQuery({
    queryKey: ['admin-lookup-agreements', company?.id],
    queryFn: () => adminLookupAPI.companyAgreements(company.id),
    enabled: !!company?.id,
    staleTime: 60_000,
  });
  const agreements = agreementsData?.data?.items || [];

  // Set defaults once lookups load
  useEffect(() => {
    if (!boardId && boards.length > 0) {
      const helpDesk =
        boards.find((b) => /help\s*desk/i.test(b.name || '')) || boards[0];
      setBoardId(String(helpDesk.id));
    }
  }, [boards, boardId]);

  useEffect(() => {
    if (boardId && statuses.length > 0 && !statusId) {
      const defaultStatus =
        statuses.find((s) => s.defaultFlag) || statuses[0];
      setStatusId(String(defaultStatus.id));
    }
  }, [boardId, statuses, statusId]);

  useEffect(() => {
    if (!sourceId && sources.length > 0) {
      const phone =
        sources.find((s) => /phone/i.test(s.name || '')) || sources[0];
      setSourceId(String(phone.id));
    }
  }, [sources, sourceId]);

  // Preload company if provided
  useEffect(() => {
    if (initialCompanyId && !company) {
      setCompany({ id: initialCompanyId, name: `Company #${initialCompanyId}` });
    }
  }, [initialCompanyId, company]);

  // ===== Optional schedule =====
  const [showSchedule, setShowSchedule] = useState(false);
  const [schedMemberId, setSchedMemberId] = useState('');
  const [schedStart, setSchedStart] = useState(nextTopOfHourLocal());
  const [schedDuration, setSchedDuration] = useState(60);
  const [schedReminder, setSchedReminder] = useState(15);
  const [schedWhereId, setSchedWhereId] = useState('');
  const [schedStatusId, setSchedStatusId] = useState('');

  const { data: wheresData } = useQuery({
    queryKey: ['admin-lookup-wheres'],
    queryFn: () => adminLookupAPI.scheduleWheres(),
    enabled: showSchedule,
    staleTime: 10 * 60 * 1000,
  });
  const { data: schedStatusesData } = useQuery({
    queryKey: ['admin-lookup-sched-statuses'],
    queryFn: () => adminLookupAPI.scheduleStatuses(),
    enabled: showSchedule,
    staleTime: 10 * 60 * 1000,
  });

  // Default the scheduled member to the ticket owner if set
  useEffect(() => {
    if (showSchedule && !schedMemberId && ownerId) setSchedMemberId(ownerId);
  }, [showSchedule, ownerId, schedMemberId]);

  // Default schedule status to "Firm" once loaded
  useEffect(() => {
    if (!schedStatusId && schedStatusesData?.data?.items?.length) {
      const firm =
        schedStatusesData.data.items.find((s) => /firm/i.test(s.name || '')) ||
        schedStatusesData.data.items[0];
      setSchedStatusId(String(firm.id));
    }
  }, [schedStatusesData, schedStatusId]);

  // Fetch the chosen member's existing schedule for the proposed day
  const schedStartIso = useMemo(() => {
    try {
      const d = new Date(schedStart);
      return Number.isNaN(d.getTime()) ? null : d.toISOString();
    } catch {
      return null;
    }
  }, [schedStart]);

  const schedEndIso = useMemo(() => {
    if (!schedStartIso) return null;
    return new Date(new Date(schedStartIso).getTime() + schedDuration * 60_000).toISOString();
  }, [schedStartIso, schedDuration]);

  const dayWindow = useMemo(() => {
    if (!schedStartIso) return null;
    const d = new Date(schedStartIso);
    const from = new Date(d);
    from.setHours(0, 0, 0, 0);
    const to = new Date(d);
    to.setHours(23, 59, 59, 999);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [schedStartIso]);

  const { data: memberScheduleData } = useQuery({
    queryKey: ['admin-member-schedule', schedMemberId, dayWindow?.from, dayWindow?.to],
    queryFn: () =>
      adminTicketsAPI.getMemberSchedule(schedMemberId, dayWindow.from, dayWindow.to),
    enabled: showSchedule && !!schedMemberId && !!dayWindow,
    staleTime: 30_000,
  });
  const memberSchedule = memberScheduleData?.data?.items || [];

  const schedConflicts = useMemo(() => {
    if (!schedStartIso || !schedEndIso) return [];
    const s = new Date(schedStartIso).getTime();
    const e = new Date(schedEndIso).getTime();
    return memberSchedule.filter((entry) => {
      const es = new Date(entry.dateStart).getTime();
      const ee = new Date(entry.dateEnd).getTime();
      return s < ee && es < e;
    });
  }, [schedStartIso, schedEndIso, memberSchedule]);

  // ===== Submit =====
  const createMutation = useMutation({
    mutationFn: (data) => adminTicketsAPI.create(data),
    onSuccess: (resp) => {
      const ticketId = resp?.data?.ticket?.id;
      if (ticketId) navigate(`/tickets/${ticketId}?from=admin`);
      onClose?.();
    },
    onError: (err) =>
      setError(err.response?.data?.error || err.response?.data?.details || 'Failed to create ticket'),
  });

  const canSubmit =
    !!company?.id &&
    !!boardId &&
    !!summary.trim() &&
    !!description.trim() &&
    !createMutation.isPending;

  const submit = (allowConflicts = false) => {
    setError(null);
    if (showSchedule && schedConflicts.length > 0 && !allowConflicts) {
      setConfirmingConflict(true);
      return;
    }

    const payload = {
      companyId: company.id,
      contactId: contact?.id || null,
      newContact: contact?.isNew ? contact.newContact : null,
      boardId: parseInt(boardId),
      statusId: statusId ? parseInt(statusId) : null,
      priorityId: priorityId ? parseInt(priorityId) : null,
      typeId: typeId ? parseInt(typeId) : null,
      subTypeId: subTypeId ? parseInt(subTypeId) : null,
      itemId: itemId ? parseInt(itemId) : null,
      ownerId: ownerId ? parseInt(ownerId) : null,
      sourceId: sourceId ? parseInt(sourceId) : null,
      agreementId: agreementId ? parseInt(agreementId) : null,
      summary: summary.trim(),
      initialDescription: description.trim(),
      initialNoteType: noteType,
    };

    createMutation.mutate(payload, {
      onSuccess: async (resp) => {
        const ticketId = resp?.data?.ticket?.id;
        // After ticket creation succeeds, optionally create a schedule entry.
        // Done in a follow-up call so a schedule failure doesn't lose the ticket.
        if (showSchedule && ticketId && schedMemberId && schedStartIso && schedEndIso) {
          try {
            await adminTicketsAPI.addSchedule(ticketId, {
              memberId: parseInt(schedMemberId),
              dateStart: schedStartIso,
              dateEnd: schedEndIso,
              reminderMinutes: schedReminder ?? null,
              whereId: schedWhereId ? parseInt(schedWhereId) : null,
              statusId: schedStatusId ? parseInt(schedStatusId) : null,
              allowScheduleConflictsFlag: allowConflicts,
            });
          } catch (e) {
            // Surface but don't block — ticket already exists
            window.alert(
              'Ticket created, but scheduling failed: ' +
                (e.response?.data?.error || e.message)
            );
          }
        }
        if (ticketId) navigate(`/tickets/${ticketId}?from=admin`);
        onClose?.();
      },
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-auto"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[95vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700 sticky top-0 bg-white dark:bg-neutral-800 z-10">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            New Ticket
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-neutral-400 hover:text-neutral-700 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* Company */}
          <Field label="Company *">
            {!company ? (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  <input
                    type="text"
                    placeholder="Search companies…"
                    value={companySearch}
                    onChange={(e) => {
                      setCompanySearch(e.target.value);
                      setShowCompanyResults(true);
                    }}
                    onFocus={() => setShowCompanyResults(true)}
                    className="w-full pl-8 pr-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
                  />
                  {searchingCompanies && (
                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-neutral-400" />
                  )}
                </div>
                {showCompanyResults && companySearch.trim().length >= 2 && (
                  <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg">
                    {companyResults.length === 0 && !searchingCompanies && (
                      <div className="px-3 py-2 text-sm text-neutral-500">No companies found</div>
                    )}
                    {companyResults.map((c) => (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => {
                          setCompany({ id: c.id, name: c.name });
                          setShowCompanyResults(false);
                          setCompanySearch('');
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 text-sm"
                      >
                        <div className="font-medium text-neutral-900 dark:text-white">
                          {c.name}
                        </div>
                        {c.identifier && (
                          <div className="text-xs text-neutral-500">{c.identifier}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between p-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-neutral-50 dark:bg-neutral-700/50">
                <div className="font-medium">{company.name}</div>
                <button
                  type="button"
                  onClick={() => {
                    setCompany(null);
                    setContact(null);
                    setAgreementId('');
                  }}
                  className="text-xs text-neutral-500 hover:text-red-500"
                >
                  Change
                </button>
              </div>
            )}
          </Field>

          {/* Contact */}
          <Field label="Contact">
            <ContactPicker companyId={company?.id} value={contact} onChange={setContact} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Source">
              <Select
                value={sourceId}
                onChange={setSourceId}
                options={sources.map((s) => ({ value: String(s.id), label: s.name }))}
                placeholder="Source…"
              />
            </Field>
            <Field label="Board *">
              <Select
                value={boardId}
                onChange={(v) => {
                  setBoardId(v);
                  setStatusId('');
                  setTypeId('');
                  setSubTypeId('');
                  setItemId('');
                }}
                options={boards.map((b) => ({ value: String(b.id), label: b.name }))}
                placeholder="Board…"
              />
            </Field>
            <Field label="Status">
              <Select
                value={statusId}
                onChange={setStatusId}
                options={statuses.map((s) => ({ value: String(s.id), label: s.name }))}
                placeholder="Status…"
                disabled={!boardId}
              />
            </Field>
            <Field label="Priority">
              <Select
                value={priorityId}
                onChange={setPriorityId}
                options={priorities.map((p) => ({ value: String(p.id), label: p.name }))}
                placeholder="Priority…"
              />
            </Field>
            <Field label="Type">
              <Select
                value={typeId}
                onChange={(v) => {
                  setTypeId(v);
                  setSubTypeId('');
                }}
                options={types.map((t) => ({ value: String(t.id), label: t.name }))}
                placeholder="Type…"
                disabled={!boardId}
              />
            </Field>
            <Field label="Sub-Type">
              <Select
                value={subTypeId}
                onChange={setSubTypeId}
                options={subTypes.map((s) => ({ value: String(s.id), label: s.name }))}
                placeholder="Sub-Type…"
                disabled={!typeId}
              />
            </Field>
            <Field label="Item">
              <Select
                value={itemId}
                onChange={setItemId}
                options={items.map((i) => ({ value: String(i.id), label: i.name }))}
                placeholder="Item…"
                disabled={!boardId}
              />
            </Field>
            <Field label="Owner (assign to)">
              <Select
                value={ownerId}
                onChange={setOwnerId}
                options={members.map((m) => ({ value: String(m.id), label: m.name }))}
                placeholder="Unassigned"
              />
            </Field>
          </div>

          <Field label="Agreement">
            <Select
              value={agreementId}
              onChange={setAgreementId}
              options={[
                { value: '', label: 'None — let CW auto-apply' },
                ...agreements.map((a) => ({ value: String(a.id), label: a.name })),
              ]}
              disabled={!company?.id}
              allowEmpty
            />
            {!company && (
              <div className="text-xs text-neutral-500 mt-1">Pick a company first.</div>
            )}
          </Field>

          <Field label="Summary *">
            <input
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value.slice(0, 100))}
              maxLength={100}
              placeholder="Short ticket summary"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
            />
            <div className="text-right text-xs text-neutral-400 mt-1">{summary.length} / 100</div>
          </Field>

          <Field label="Initial description *">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="What did the caller say?"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
            />
          </Field>

          <Field label="Initial note type">
            <div className="flex gap-3">
              {NOTE_TYPES.map((t) => (
                <label key={t.value} className="flex items-center gap-1 text-sm">
                  <input
                    type="radio"
                    name="noteType"
                    value={t.value}
                    checked={noteType === t.value}
                    onChange={() => setNoteType(t.value)}
                  />
                  {t.label}
                </label>
              ))}
            </div>
            <div className="text-xs text-neutral-500 mt-1">
              Will be authored as:{' '}
              <strong>
                {currentUser?.firstName || currentUser?.first_name || 'Portal User'}{' '}
                {currentUser?.lastName || currentUser?.last_name || ''}
              </strong>
            </div>
          </Field>

          {/* Optional schedule entry */}
          <Collapsible
            label="Schedule the resource"
            open={showSchedule}
            onToggle={() => setShowSchedule((v) => !v)}
          >
            <div className="grid grid-cols-2 gap-3">
              <Field label="Member *">
                <Select
                  value={schedMemberId}
                  onChange={setSchedMemberId}
                  options={members.map((m) => ({ value: String(m.id), label: m.name }))}
                  placeholder="Pick a CW member…"
                />
              </Field>
              <Field label="Start *">
                <input
                  type="datetime-local"
                  value={schedStart}
                  onChange={(e) => setSchedStart(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
                />
              </Field>
              <Field label="Duration">
                <Select
                  value={String(schedDuration)}
                  onChange={(v) => setSchedDuration(parseInt(v))}
                  options={DURATIONS.map((d) => ({ value: String(d.minutes), label: d.label }))}
                />
              </Field>
              <Field label="Reminder">
                <Select
                  value={schedReminder == null ? '' : String(schedReminder)}
                  onChange={(v) => setSchedReminder(v === '' ? null : parseInt(v))}
                  options={REMINDERS.map((r) => ({
                    value: r.minutes == null ? '' : String(r.minutes),
                    label: r.label,
                  }))}
                  allowEmpty
                />
              </Field>
              <Field label="Location">
                <Select
                  value={schedWhereId}
                  onChange={setSchedWhereId}
                  options={(wheresData?.data?.items || []).map((w) => ({
                    value: String(w.id),
                    label: w.name,
                  }))}
                  placeholder="Anywhere"
                  allowEmpty
                />
              </Field>
              <Field label="Status">
                <Select
                  value={schedStatusId}
                  onChange={setSchedStatusId}
                  options={(schedStatusesData?.data?.items || []).map((s) => ({
                    value: String(s.id),
                    label: s.name,
                  }))}
                  placeholder="Status…"
                />
              </Field>
            </div>
            {schedMemberId && schedStartIso && (
              <div className="mt-3">
                <MemberDayTimeline
                  dayDate={new Date(schedStartIso)}
                  scheduleEntries={memberSchedule}
                  proposed={{ dateStart: schedStartIso, dateEnd: schedEndIso }}
                />
              </div>
            )}
          </Collapsible>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200 dark:border-neutral-700 sticky bottom-0 bg-white dark:bg-neutral-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => submit(false)}
            className="px-4 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
          >
            {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Create Ticket
          </button>
        </div>
      </div>

      {confirmingConflict && (
        <ConflictDialog
          conflicts={schedConflicts}
          onCancel={() => setConfirmingConflict(false)}
          onConfirm={() => {
            setConfirmingConflict(false);
            submit(true);
          }}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, placeholder, disabled, allowEmpty }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm disabled:opacity-50"
    >
      {(placeholder || allowEmpty) && (
        <option value="">{placeholder || ''}</option>
      )}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Collapsible({ label, open, onToggle, badge, children }) {
  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 text-left text-sm font-medium text-neutral-700 dark:text-neutral-200"
      >
        <span className="flex items-center gap-2">
          {label}
          {badge && (
            <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 rounded">
              {badge}
            </span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && <div className="p-3 pt-0">{children}</div>}
    </div>
  );
}

function ConflictDialog({ conflicts, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 mb-2">
          <AlertTriangle className="w-5 h-5" />
          <h3 className="font-semibold text-neutral-900 dark:text-white">Schedule conflict</h3>
        </div>
        <p className="text-sm text-neutral-700 dark:text-neutral-300">
          The proposed schedule overlaps with:
        </p>
        <ul className="mt-2 text-sm text-red-600 dark:text-red-400 list-disc list-inside">
          {conflicts.map((c) => (
            <li key={c.id}>
              {c.summary || `${c.type || 'Entry'} #${c.objectId || ''}`} (
              {new Date(c.dateStart).toLocaleTimeString()} –{' '}
              {new Date(c.dateEnd).toLocaleTimeString()})
            </li>
          ))}
        </ul>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg"
          >
            Pick a different time
          </button>
          <button
            onClick={onConfirm}
            className="px-3 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
          >
            Book anyway
          </button>
        </div>
      </div>
    </div>
  );
}
